/**
 * Cart Celebration — 3-Layer Dopamine Animation
 *
 * Fires immediately on add-to-cart button click (optimistic — no API wait).
 * Firing at click time instead of on cartUpdate means the animation plays
 * BEFORE the cart drawer slides in, so all three layers are visible.
 *
 * Three animation layers:
 *   Layer 1 — Button bounces and shows "✓ Added!" for 1.5 s, then restores
 *   Layer 2 — Brand-coloured confetti particles burst from the button centre
 *   Layer 3 — Cart icon does a spring bounce
 *
 * Uses document-level capture delegation: works regardless of when buttons
 * are added to the DOM, and survives any event.stopPropagation calls.
 *
 * CSS is injected inline at init time so this file is self-contained. This
 * makes it immune to service-worker stale-cache scenarios where the external
 * cart-celebration.css may not be loaded (e.g. the SW cached the collection
 * page HTML before cart-celebration.css was added to theme.liquid).
 */

(function () {
  'use strict';

  /* ── Inject required CSS (self-contained; no external stylesheet needed) ── */

  (function injectStyles() {
    if (document.getElementById('cart-celebration-css')) return;
    const s = document.createElement('style');
    s.id = 'cart-celebration-css';
    /* prettier-ignore */
    s.textContent =
      /* Layer 1 — button success flash */
      '.btn--cart-success{background:#b87878!important;border-color:#b87878!important;color:#fff!important;transform:scale(1.04)!important;transition:background .1s ease,border-color .1s ease,transform .15s cubic-bezier(.34,1.56,.64,1)!important}' +

      /* Layer 2 — confetti particles */
      '.cart-confetti-particle{position:fixed;pointer-events:none;border-radius:50%;background:var(--cc-color,#c19393);z-index:9999;animation:cc-burst .88s ease-out forwards}' +
      '@keyframes cc-burst{0%{opacity:1;transform:scale(.3)}35%{opacity:1;transform:translate(calc(var(--cc-tx)*.55),calc(var(--cc-ty)*.55)) scale(1.2)}100%{opacity:0;transform:translate(var(--cc-tx),calc(var(--cc-ty) + 20px)) scale(.4)}}' +

      /* Layer 3 — cart icon spring */
      '.cart-icon--spring{animation:cc-spring .52s cubic-bezier(.36,.07,.19,.97) forwards}' +
      '@keyframes cc-spring{0%{transform:scale(1)}18%{transform:scale(1.25)}36%{transform:scale(.86)}54%{transform:scale(1.13)}72%{transform:scale(.95)}88%{transform:scale(1.04)}100%{transform:scale(1)}}' +

      /* Reduced motion — keep text swap, skip movement */
      '@media (prefers-reduced-motion:reduce){.cart-confetti-particle{display:none}.btn--cart-success{transform:none!important;transition:background .1s ease,border-color .1s ease!important}.cart-icon--spring{animation:none}}';
    document.head.appendChild(s);
  })();

  /* Brand palette — dusty rose, cream, taupe, deep rose */
  const COLORS = ['#c19393', '#d4a0a0', '#f0e8e8', '#9e8e7e', '#e8d5d5', '#b0a0a0'];
  const PARTICLE_COUNT = 24;

  /* Cooldown: ignore rapid re-clicks within this window (ms) */
  const COOLDOWN_MS = 2000;
  let lastCelebrationTime = 0;

  /* ── Layer 1: button success state ─────────────────────────────── */

  function animateButton(btn) {
    /* Find the first span with visible text */
    const spans = btn.querySelectorAll('span');
    let span = null;
    let originalText = '';
    for (let i = 0; i < spans.length; i++) {
      const t = spans[i].textContent.trim();
      if (t) {
        span = spans[i];
        originalText = t;
        break;
      }
    }
    if (!span) return;

    btn.classList.add('btn--cart-success');
    span.textContent = '\u2713 Added!';

    setTimeout(function () {
      btn.classList.remove('btn--cart-success');
      span.textContent = originalText;
    }, 1500);
  }

  /* ── Layer 2: confetti burst ────────────────────────────────────── */

  function burstConfetti(btn) {
    const rect = btn.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      spawnParticle(cx, cy, i);
    }
  }

  function spawnParticle(cx, cy, index) {
    const p = document.createElement('div');
    p.className = 'cart-confetti-particle';

    /* Vary particle size 6–11 px for organic feel */
    const size = 6 + Math.round(Math.random() * 5);
    p.style.width = size + 'px';
    p.style.height = size + 'px';

    const color = COLORS[Math.floor(Math.random() * COLORS.length)];
    p.style.setProperty('--cc-color', color);

    /* Spread evenly around 360° with slight random jitter */
    const angle = (Math.PI * 2 * index) / PARTICLE_COUNT + (Math.random() - 0.5) * 0.45;
    const dist = 55 + Math.random() * 70;
    p.style.setProperty('--cc-tx', (Math.cos(angle) * dist).toFixed(1) + 'px');
    p.style.setProperty('--cc-ty', (Math.sin(angle) * dist).toFixed(1) + 'px');

    /* Stagger launch slightly */
    p.style.animationDelay = (Math.random() * 0.07).toFixed(3) + 's';

    /* Position centred on the button */
    p.style.left = (cx - size / 2).toFixed(1) + 'px';
    p.style.top = (cy - size / 2).toFixed(1) + 'px';

    document.body.appendChild(p);
    /* Remove slightly after animation ends */
    setTimeout(function () {
      if (p.parentNode) p.remove();
    }, 1100);
  }

  /* ── Layer 3: cart icon spring ──────────────────────────────────── */

  function bounceCartIcon() {
    const cartBtn = document.querySelector('.cart-drawer-button');
    if (!cartBtn) return;
    /* Remove first to allow re-trigger if clicked quickly */
    cartBtn.classList.remove('cart-icon--spring');
    void cartBtn.offsetWidth; /* Force reflow so animation restarts cleanly */
    cartBtn.classList.add('cart-icon--spring');
    cartBtn.addEventListener(
      'animationend',
      function () {
        cartBtn.classList.remove('cart-icon--spring');
      },
      { once: true }
    );
  }

  /* ── Main celebration ───────────────────────────────────────────── */

  function celebrate(btn) {
    animateButton(btn);
    burstConfetti(btn);
    bounceCartIcon();
  }

  /* ── Document-level click delegation (capture phase) ───────────── */

  document.addEventListener(
    'click',
    function (e) {
      const btn = e.target.closest('.product-form__submit, button[name="add"]');
      if (!btn) return;

      /* Skip if disabled (sold out) */
      if (btn.disabled) return;

      /* Cooldown guard against rapid double-clicks */
      const now = Date.now();
      if (now - lastCelebrationTime < COOLDOWN_MS) return;
      lastCelebrationTime = now;

      celebrate(btn);
    },
    true /* capture phase — fires before any other handler */
  );
})();
